import GoogleBusinessService from './googleAuth';
import FacebookBusinessService from './facebookAuth';
import TwitterAuthService from './twitterAuth';
import LinkedInAuthService from './linkedinAuth';
import ShopifyAuthService from './shopifyAuth';
import {
  WAITLIST_ONLY,
  AUTH_STORAGE_KEYS,
  WAITLIST_STORAGE_KEY,
} from '../config';

class AuthService {
  constructor() {
    this.googleService = new GoogleBusinessService();
    this.facebookService = new FacebookBusinessService();
    this.twitterService = new TwitterAuthService();
    this.linkedinService = new LinkedInAuthService();
    this.shopifyService = new ShopifyAuthService();
    this.currentProvider = null;
    this.businessData = [];
  }

  /** Scrub any leftover OAuth/demo tokens so they cannot unlock UI. */
  clearAllAuthStorage() {
    if (typeof window === 'undefined' || !window.localStorage) return;
    AUTH_STORAGE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_) {
        /* ignore */
      }
    });
    this.businessData = [];
    this.currentProvider = null;
  }

  /**
   * Waitlist only — stores email, never writes auth tokens or business session.
   */
  async joinWaitlist(email) {
    const trimmed = String(email || '').trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      throw new Error('Enter a valid email address');
    }

    // Ensure no auth side-effects
    this.clearAllAuthStorage();

    let list = [];
    try {
      const raw = localStorage.getItem(WAITLIST_STORAGE_KEY);
      list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];
    } catch (_) {
      list = [];
    }

    const already = list.some(
      (row) => (typeof row === 'string' ? row : row?.email) === trimmed
    );
    if (!already) {
      list.push({ email: trimmed, joinedAt: new Date().toISOString() });
      localStorage.setItem(WAITLIST_STORAGE_KEY, JSON.stringify(list));
    }

    // No tokens, no isAuthenticated path
    return { ok: true, email: trimmed, already };
  }

  async initiateAuth(provider, email = null) {
    if (WAITLIST_ONLY) {
      this.clearAllAuthStorage();
      const p = String(provider || '').toLowerCase();
      if ((p === 'email' || p === 'waitlist') && email) {
        // Never return business array — joinWaitlist only
        await this.joinWaitlist(email);
        return { waitlist: true, email: String(email).trim().toLowerCase() };
      }
      throw new Error('Login is disabled. Join the waitlist for early access.');
    }

    this.currentProvider = provider.toLowerCase();
    
    try {
      switch (this.currentProvider) {
        case 'google':
          try {
            const googleAuth = await this.googleService.initializeAuth();
            window.location.href = googleAuth.authUrl;
          } catch (authError) {
            // If OAuth isn't configured, use demo mode
            if (authError.message.includes('Client ID not configured')) {
              console.warn('Google OAuth not configured, using demo mode');
              return this.handleDemoAuth('google');
            }
            throw authError;
          }
          break;
          
        case 'facebook':
          try {
            const facebookAuth = await this.facebookService.initializeAuth();
            window.location.href = facebookAuth.authUrl;
          } catch (authError) {
            console.warn('Facebook OAuth not configured, using demo mode');
            return this.handleDemoAuth('facebook');
          }
          break;
          
        case 'instagram':
          try {
            // Instagram uses Facebook's authentication
            const instagramAuth = await this.facebookService.initializeAuth();
            window.location.href = instagramAuth.authUrl;
          } catch (authError) {
            console.warn('Instagram OAuth not configured, using demo mode');
            return this.handleDemoAuth('instagram');
          }
          break;
          
        case 'email':
          console.log('Email login with:', email);
          return this.handleDemoAuth('email', email);
          
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
    } catch (error) {
      console.error(`Error initiating ${provider} auth:`, error);
      throw error;
    }
  }

  async handleDemoAuth(provider, email = null) {
    if (WAITLIST_ONLY) {
      this.clearAllAuthStorage();
      throw new Error('Login is disabled. Join the waitlist for early access.');
    }

    // Simulate authentication for demo purposes
    console.log(`Demo authentication for ${provider}`, email ? `with email: ${email}` : '');
    
    const demoBusinessData = this.generateDemoBusinessData(provider, email);
    this.businessData = [demoBusinessData];
    
    // Store demo tokens
    const demoTokens = {
      access_token: `demo_${provider}_token_${Date.now()}`,
      token_type: 'Bearer',
      expires_in: 3600,
      demo: true,
      email: email
    };
    
    localStorage.setItem(`${provider}_tokens`, JSON.stringify(demoTokens));
    
    return [demoBusinessData];
  }

  generateDemoBusinessData(provider, email = null) {
    const businesses = {
      google: {
        id: 'demo_google_business',
        name: 'Demo Restaurant (Google)',
        address: '123 Demo Street, New York, NY 10001',
        phone: '+1 (555) 123-4567',
        website: 'https://demo-restaurant.com',
        category: 'Restaurant',
        description: 'Demo business data from Google My Business',
        position: { lat: 40.7128, lng: -74.0060 },
        rating: 4.5,
        reviewCount: 150,
        source: 'google',
        hours: {
          monday: { open: '09:00', close: '21:00', closed: false },
          tuesday: { open: '09:00', close: '21:00', closed: false },
          wednesday: { open: '09:00', close: '21:00', closed: false },
          thursday: { open: '09:00', close: '21:00', closed: false },
          friday: { open: '09:00', close: '22:00', closed: false },
          saturday: { open: '09:00', close: '22:00', closed: false },
          sunday: { open: '10:00', close: '20:00', closed: false }
        }
      },
      facebook: {
        id: 'demo_facebook_business',
        name: 'Demo Cafe (Facebook)',
        address: '456 Social Avenue, San Francisco, CA 94102',
        phone: '+1 (555) 987-6543',
        website: 'https://demo-cafe.com',
        category: 'Cafe',
        description: 'Demo business data from Facebook Pages',
        position: { lat: 37.7749, lng: -122.4194 },
        rating: 4.8,
        reviewCount: 89,
        source: 'facebook',
        hours: {
          monday: { open: '07:00', close: '19:00', closed: false },
          tuesday: { open: '07:00', close: '19:00', closed: false },
          wednesday: { open: '07:00', close: '19:00', closed: false },
          thursday: { open: '07:00', close: '19:00', closed: false },
          friday: { open: '07:00', close: '20:00', closed: false },
          saturday: { open: '08:00', close: '20:00', closed: false },
          sunday: { open: '08:00', close: '18:00', closed: false }
        }
      },
      instagram: {
        id: 'demo_instagram_business',
        name: 'Demo Boutique (Instagram)',
        address: '789 Fashion Street, Los Angeles, CA 90210',
        phone: '+1 (555) 456-7890',
        website: 'https://demo-boutique.com',
        category: 'Retail',
        description: 'Demo business data from Instagram Business',
        position: { lat: 34.0522, lng: -118.2437 },
        rating: 4.6,
        reviewCount: 203,
        source: 'instagram',
        hours: {
          monday: { open: '10:00', close: '20:00', closed: false },
          tuesday: { open: '10:00', close: '20:00', closed: false },
          wednesday: { open: '10:00', close: '20:00', closed: false },
          thursday: { open: '10:00', close: '20:00', closed: false },
          friday: { open: '10:00', close: '21:00', closed: false },
          saturday: { open: '10:00', close: '21:00', closed: false },
          sunday: { open: '12:00', close: '18:00', closed: false }
        }
      },
      twitter: {
        id: 'demo_twitter_business',
        name: 'Demo Social Media (Twitter)',
        address: '789 Social Street, Austin, TX 78701',
        phone: '+1 (555) 333-7777',
        website: 'https://demo-social.com',
        category: 'Digital Marketing',
        description: 'Demo business data from Twitter Business',
        position: { lat: 30.2672, lng: -97.7431 },
        rating: 4.3,
        reviewCount: 89,
        source: 'twitter',
        hours: {
          monday: { open: '09:00', close: '17:00', closed: false },
          tuesday: { open: '09:00', close: '17:00', closed: false },
          wednesday: { open: '09:00', close: '17:00', closed: false },
          thursday: { open: '09:00', close: '17:00', closed: false },
          friday: { open: '09:00', close: '17:00', closed: false },
          saturday: { open: '10:00', close: '15:00', closed: false },
          sunday: { open: '10:00', close: '15:00', closed: true }
        }
      },
      linkedin: {
        id: 'demo_linkedin_business',
        name: 'Demo Professional Services (LinkedIn)',
        address: '456 Business Plaza, Chicago, IL 60601',
        phone: '+1 (555) 444-8888',
        website: 'https://demo-professional.com',
        category: 'Professional Services',
        description: 'Demo business data from LinkedIn Business',
        position: { lat: 41.8781, lng: -87.6298 },
        rating: 4.7,
        reviewCount: 178,
        source: 'linkedin',
        hours: {
          monday: { open: '08:00', close: '18:00', closed: false },
          tuesday: { open: '08:00', close: '18:00', closed: false },
          wednesday: { open: '08:00', close: '18:00', closed: false },
          thursday: { open: '08:00', close: '18:00', closed: false },
          friday: { open: '08:00', close: '17:00', closed: false },
          saturday: { open: '09:00', close: '15:00', closed: false },
          sunday: { open: '09:00', close: '15:00', closed: true }
        }
      },
      shopify: {
        id: 'demo_shopify_business',
        name: 'Demo E-commerce Store (Shopify)',
        address: '789 Commerce Ave, Portland, OR 97205',
        phone: '+1 (555) 555-9999',
        website: 'https://demo-store.myshopify.com',
        category: 'E-commerce',
        description: 'Demo business data from Shopify Store',
        position: { lat: 45.5152, lng: -122.6784 },
        rating: 4.4,
        reviewCount: 267,
        source: 'shopify',
        hours: {
          monday: { open: '00:00', close: '23:59', closed: false },
          tuesday: { open: '00:00', close: '23:59', closed: false },
          wednesday: { open: '00:00', close: '23:59', closed: false },
          thursday: { open: '00:00', close: '23:59', closed: false },
          friday: { open: '00:00', close: '23:59', closed: false },
          saturday: { open: '00:00', close: '23:59', closed: false },
          sunday: { open: '00:00', close: '23:59', closed: false }
        }
      },
      email: {
        id: 'demo_email_business',
        name: 'Demo Business (Email)',
        address: '123 Business Street, Demo City, DC 12345',
        phone: '+1 (555) 123-4567',
        website: 'https://demo-business.com',
        category: 'Business',
        description: `Demo business data for email: ${email || 'demo@example.com'}`,
        position: { lat: 39.8283, lng: -98.5795 },
        rating: 4.7,
        reviewCount: 125,
        source: 'email',
        email: email,
        hours: {
          monday: { open: '09:00', close: '17:00', closed: false },
          tuesday: { open: '09:00', close: '17:00', closed: false },
          wednesday: { open: '09:00', close: '17:00', closed: false },
          thursday: { open: '09:00', close: '17:00', closed: false },
          friday: { open: '09:00', close: '17:00', closed: false },
          saturday: { open: '10:00', close: '15:00', closed: false },
          sunday: { open: '10:00', close: '15:00', closed: true }
        }
      }
    };

    return businesses[provider] || businesses.google;
  }

  async loadTwitterBusinessData() {
    try {
      const profile = await this.twitterService.getUserProfile();
      const twitterData = {
        id: profile.id,
        name: `${profile.name} (@${profile.username})`,
        description: profile.description,
        followers: profile.followers,
        following: profile.following,
        tweets: profile.tweets,
        profileImageUrl: profile.profileImageUrl,
        url: profile.url,
        location: profile.location,
        source: 'twitter'
      };
      this.businessData = [twitterData];
      return [twitterData];
    } catch (error) {
      console.error('Error loading Twitter business data:', error);
      throw error;
    }
  }

  async loadLinkedInBusinessData() {
    try {
      const profile = await this.linkedinService.getUserProfile();
      const companies = await this.linkedinService.getCompanyPages();
      
      const linkedinData = companies.length > 0 ? companies : [{
        id: profile.id,
        name: `${profile.name} (Personal Profile)`,
        description: 'LinkedIn Personal Profile',
        email: profile.email,
        profileImageUrl: profile.profileImageUrl,
        source: 'linkedin'
      }];
      
      this.businessData = linkedinData;
      return linkedinData;
    } catch (error) {
      console.error('Error loading LinkedIn business data:', error);
      throw error;
    }
  }

  async loadShopifyBusinessData() {
    try {
      const shopInfo = await this.shopifyService.getShopInfo();
      const analytics = await this.shopifyService.getAnalytics();
      
      const shopifyData = {
        ...shopInfo,
        analytics: analytics,
        source: 'shopify'
      };
      
      this.businessData = [shopifyData];
      return [shopifyData];
    } catch (error) {
      console.error('Error loading Shopify business data:', error);
      throw error;
    }
  }

  async handleAuthCallback(provider, code) {
    if (WAITLIST_ONLY) {
      this.clearAllAuthStorage();
      throw new Error('Login is disabled. Join the waitlist for early access.');
    }
    try {
      switch (provider.toLowerCase()) {
        case 'google':
          await this.googleService.handleAuthCallback(code);
          return await this.loadGoogleBusinessData();
          
        case 'facebook':
        case 'instagram':
          await this.facebookService.handleAuthCallback(code);
          return await this.loadFacebookBusinessData();
          
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
    } catch (error) {
      console.error(`Error handling ${provider} auth callback:`, error);
      throw error;
    }
  }

  async loadGoogleBusinessData() {
    try {
      const locations = await this.googleService.getBusinessLocations();
      this.businessData = locations;
      return locations;
    } catch (error) {
      console.error('Error loading Google business data:', error);
      throw error;
    }
  }

  async loadFacebookBusinessData() {
    try {
      const pages = await this.facebookService.getBusinessPages();
      
      // Enrich with Instagram data
      const enrichedPages = await Promise.all(
        pages.map(async (page) => {
          try {
            const instagramAccount = await this.facebookService.getInstagramAccount(page.id);
            return {
              ...page,
              instagramData: instagramAccount,
            };
          } catch (error) {
            console.warn(`Failed to fetch Instagram data for page ${page.id}:`, error);
            return page;
          }
        })
      );

      this.businessData = enrichedPages;
      return enrichedPages;
    } catch (error) {
      console.error('Error loading Facebook business data:', error);
      throw error;
    }
  }

  async getBusinessInsights(businessId, provider) {
    try {
      switch (provider.toLowerCase()) {
        case 'facebook':
          return await this.facebookService.getPageInsights(businessId);
        case 'google':
          // Google My Business API doesn't provide insights in the same way
          // You would need to use Google Analytics or Search Console APIs
          return [];
        default:
          return [];
      }
    } catch (error) {
      console.error('Error fetching business insights:', error);
      return [];
    }
  }

  getCurrentBusinessData() {
    if (WAITLIST_ONLY) return [];
    return this.businessData;
  }

  getAuthenticatedProviders() {
    if (WAITLIST_ONLY) {
      return { google: false, facebook: false, instagram: false, email: false };
    }
    return {
      google: this.googleService.isAuthenticated() || this.hasDemoTokens('google'),
      facebook: this.facebookService.isAuthenticated() || this.hasDemoTokens('facebook'),
      instagram: this.facebookService.isAuthenticated() || this.hasDemoTokens('instagram'),
      email: this.hasDemoTokens('email'),
    };
  }

  hasDemoTokens(provider) {
    const tokens = localStorage.getItem(`${provider}_tokens`);
    if (tokens) {
      try {
        const parsed = JSON.parse(tokens);
        return parsed.demo === true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  async logout(provider) {
    switch (provider.toLowerCase()) {
      case 'google':
        this.googleService.clearTokens();
        localStorage.removeItem('google_tokens');
        break;
      case 'facebook':
        this.facebookService.clearTokens();
        localStorage.removeItem('facebook_tokens');
        break;
      case 'instagram':
        localStorage.removeItem('instagram_tokens');
        break;
      case 'email':
        localStorage.removeItem('email_tokens');
        break;
      case 'all':
        this.googleService.clearTokens();
        this.facebookService.clearTokens();
        localStorage.removeItem('google_tokens');
        localStorage.removeItem('facebook_tokens');
        localStorage.removeItem('instagram_tokens');
        localStorage.removeItem('email_tokens');
        break;
      default:
        console.warn(`Unknown provider for logout: ${provider}`);
        break;
    }
    
    this.businessData = [];
    this.currentProvider = null;
  }

  async refreshTokens() {
    const providers = this.getAuthenticatedProviders();
    
    if (providers.google) {
      try {
        await this.googleService.refreshTokens();
      } catch (error) {
        console.error('Failed to refresh Google tokens:', error);
      }
    }
    
    // Facebook tokens typically have longer expiry times
    // and auto-refresh through the SDK
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;